import { maybe, rd } from '@passionware/monads';
import { CalendarCheck } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportEntityType } from 'v5/api/report-templates/reportTemplates.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import type { ReportEditorState } from 'v5/core/reporting/reportTemplateQuery';
import { FormField } from 'v5/design-system/Form';
import { Skeleton } from 'v5/design-system/Skeleton';
import {
  type ResourceFiltersServices,
  useResourceFilters
} from 'v5/features/resources/useResourceFilters';
import { FilterWidget } from 'v5/patterns/filters/FilterWidget';
import type {
  FilterConfigBase,
  FilterGroupDefinition
} from 'v5/patterns/filters/FilterWidget.types';
import { UnsupportedFilterWidget } from 'v5/patterns/filters/UnsupportedFilterWidget';
import { useFieldContext } from 'v5/patterns/forms/schema-form/FieldContext';
import { FieldControlBase } from 'v5/patterns/forms/schema-form/fields/FieldControlBase';
import type { FieldFrameSpec } from 'v5/patterns/forms/schema-form/fields/fieldComponent.types';
import { WithServices } from 'v5/platform/ts/services';
import {
  isKnownRelativeDate,
  REPORT_RELATIVE_DATES,
  reportDateFieldsOf
} from './reportDateFilter';
import {
  createReportBookedFilterConfig,
  createReportDateFilterConfig
} from './reportDateFilterConfig';
import {
  REPORT_BOOKED_KEY,
  reportDateKeyOf,
  reportEditorStateWith,
  reportFilterBarOf
} from './reportFilterBar';
import { reportResourceFilterConfigsOf } from './reportResourceFilterConfigs';

type DateFieldLabelKey =
  | 'created_at'
  | 'booking_date'
  | 'production_date'
  | 'callsheet_date';

const isDateFieldLabelKey = (field: string): field is DateFieldLabelKey =>
  ['created_at', 'booking_date', 'production_date', 'callsheet_date'].includes(
    field
  );

export type ReportFiltersFieldProps = WithServices<
  [ResourceFiltersServices]
> & {
  spec: FieldFrameSpec;
  /** A {@link ReportEditorState}. */
  path: string;
  entityType: ReportEntityType;
  fieldTypes: FieldNameTypeMapping;
};

/**
 * A report template's conditions as one filter bar: the date chip for every entity, the resource
 * filters for Resources, and the stored conditions the editor can't express as read-only chips
 * that can be removed.
 */
export function ReportFiltersField({
  spec,
  path,
  entityType,
  fieldTypes,
  services
}: ReportFiltersFieldProps) {
  return (
    <FormField
      name={path}
      render={({ field }) => (
        <FieldControlBase spec={spec}>
          <ReportFilterBar
            state={field.value}
            onStateChange={field.onChange}
            entityType={entityType}
            fieldTypes={fieldTypes}
            services={services}
          />
        </FieldControlBase>
      )}
    />
  );
}

function ReportFilterBar({
  state,
  onStateChange,
  entityType,
  fieldTypes,
  services,
  ...slotProps
}: {
  state: ReportEditorState;
  onStateChange: (state: ReportEditorState) => void;
  entityType: ReportEntityType;
  fieldTypes: FieldNameTypeMapping;
} & WithServices<[ResourceFiltersServices]>) {
  const { t } = useTranslation('reporting', {
    keyPrefix: 'ReportTemplateEditor'
  });
  const readOnly = useFieldContext().mode === 'display';
  const organizationId = services.appScopeService.useOrganizationId();
  const resourceRequest = useMemo(
    () =>
      entityType === 'contact'
        ? {
            type: 'resource' as const,
            scope: { level: 'organization' as const, organizationId }
          }
        : maybe.ofAbsent(),
    [entityType, organizationId]
  );
  const resourceFilters = useResourceFilters(resourceRequest, services);

  const dateLabel = useCallback(
    (field: string) =>
      isDateFieldLabelKey(field) ? t(`dateFields.${field}`) : field,
    [t]
  );
  const storedRelative =
    state.date?.kind === 'relative' && !isKnownRelativeDate(state.date.relative)
      ? state.date.relative
      : null;
  const dateConfigs = useMemo(() => {
    const presets = [
      ...REPORT_RELATIVE_DATES.map(value => ({
        value,
        label: t(`relativeDates.${value}`)
      })),
      // a stored value this client doesn't know stays as it is
      ...(storedRelative === null
        ? []
        : [{ value: storedRelative, label: storedRelative }])
    ];
    return Object.fromEntries(
      reportDateFieldsOf(entityType).map(field => [
        reportDateKeyOf(field),
        createReportDateFilterConfig(dateLabel(field), presets)
      ])
    );
  }, [entityType, storedRelative, dateLabel, t]);
  const allResourceConfigs = useMemo(
    (): Record<string, FilterConfigBase> =>
      rd.tryMap(resourceFilters, filters => filters.configs) ?? {},
    [resourceFilters]
  );
  const resourceConfigs = useMemo(
    () => reportResourceFilterConfigsOf(allResourceConfigs, fieldTypes),
    [allResourceConfigs, fieldTypes]
  );
  const hasBooked = state.booked !== null;
  const config = useMemo(
    (): Record<string, FilterConfigBase> => ({
      ...dateConfigs,
      ...resourceConfigs,
      // its statuses come only with a stored condition: a new one can't be added
      ...(hasBooked && {
        [REPORT_BOOKED_KEY]: {
          ...createReportBookedFilterConfig(t('booked')),
          icon: <CalendarCheck />
        }
      })
    }),
    [dateConfigs, resourceConfigs, hasBooked, t]
  );
  const resourceGroups = useMemo(
    () =>
      (rd.tryMap(resourceFilters, filters => filters.groups) ?? []).flatMap(
        (group): FilterGroupDefinition[] => {
          const children = group.children.filter(key => key in resourceConfigs);
          return children.length > 0 ? [{ ...group, children }] : [];
        }
      ),
    [resourceFilters, resourceConfigs]
  );
  const groups = useMemo(
    (): FilterGroupDefinition[] => [
      { name: t('dateGroup'), children: Object.keys(dateConfigs) },
      ...resourceGroups
    ],
    [t, dateConfigs, resourceGroups]
  );

  if (
    entityType === 'contact' &&
    !rd.isSuccess(resourceFilters) &&
    !rd.isError(resourceFilters)
  ) {
    return <Skeleton className="h-8 w-64" />;
  }

  const order = [
    ...Object.keys(dateConfigs),
    ...resourceGroups.flatMap(group => group.children),
    ...(hasBooked ? [REPORT_BOOKED_KEY] : [])
  ];
  const bar = reportFilterBarOf(state);
  const labelOf = (key: string) =>
    allResourceConfigs[key]?.label ?? config[key]?.label ?? key;

  return (
    <fieldset
      {...slotProps}
      disabled={readOnly}
      className="flex min-w-0 flex-wrap gap-2"
    >
      <FilterWidget
        className="contents"
        config={config}
        order={[...new Set(order)]}
        groups={groups}
        value={bar.value}
        onValueChange={next =>
          onStateChange(reportEditorStateWith(state, bar.value, next))
        }
      />
      {entityType === 'contact' && rd.isError(resourceFilters) && (
        <p className="self-center text-sm text-fg-danger">
          {resourceFilters.error.message}
        </p>
      )}
      {bar.unreadable.map(part => (
        <UnsupportedFilterWidget
          key={part}
          filterField={part}
          filterName={
            part === 'date' && state.date
              ? dateLabel(state.date.field)
              : t('booked')
          }
          value={part === 'date' ? state.date : state.booked}
          onRemove={() =>
            onStateChange(
              part === 'date'
                ? { ...state, date: null }
                : { ...state, booked: null }
            )
          }
        />
      ))}
      {state.unsupported.map(({ key, filter }) => (
        <UnsupportedFilterWidget
          key={key}
          filterField={key}
          filterName={labelOf(key)}
          value={filter}
          onRemove={() =>
            onStateChange({
              ...state,
              unsupported: state.unsupported.filter(kept => kept.key !== key)
            })
          }
        />
      ))}
    </fieldset>
  );
}
